import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Button,
  InputAdornment,
  Alert
} from '@mui/material';
import { 
  Search as SearchIcon,
  MyLocation as MyLocationIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const SimpleMapCoordinateSelector = ({ 
  initialCoordinates,
  onCoordinatesChange,
  initialAddress = '',
  height = 400,
  width = '100%',
  zoom = 15,
  apiKey
}) => {
  
  // Default coordinates (London) if none provided
  const defaultCoords = { lat: 51.509865, lng: -0.118092 };
  
  // Helper function to safely convert coordinates to numbers
  const parseCoordinates = (coords) => {
    if (!coords) return null;
    
    const lat = typeof coords.lat === 'string' ? parseFloat(coords.lat) : coords.lat;
    const lng = typeof coords.lng === 'string' ? parseFloat(coords.lng) : coords.lng;
    
    // Check if coordinates are valid numbers and within valid ranges
    if (
      typeof lat === 'number' && 
      typeof lng === 'number' && 
      !isNaN(lat) && 
      !isNaN(lng) &&
      lat >= -90 && lat <= 90 &&
      lng >= -180 && lng <= 180
    ) {
      return { lat, lng };
    }
    
    return null;
  };

  const parsedCoordinates = parseCoordinates(initialCoordinates);
  const coordinates = parsedCoordinates || defaultCoords;

  const [currentPosition, setCurrentPosition] = useState(coordinates);
  const [searchAddress, setSearchAddress] = useState(initialAddress);
  const [error, setError] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Update position when initialCoordinates change
  useEffect(() => {
    const newParsedCoordinates = parseCoordinates(initialCoordinates);
    if (newParsedCoordinates) {
      setCurrentPosition(newParsedCoordinates);
    }
  }, [initialCoordinates]);

  // Update search address when initialAddress changes
  useEffect(() => {
    if (initialAddress && initialAddress !== searchAddress) {
      setSearchAddress(initialAddress);
    }
  }, [initialAddress]);

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const containerStyle = {
    width: '100%',
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: '8px'
  };

  const options = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy'
  };

  // Handle map click
  const handleMapClick = useCallback((event) => {
    const newPosition = {
      lat: parseFloat(event.latLng.lat().toFixed(6)), // Round to 6 decimal places
      lng: parseFloat(event.latLng.lng().toFixed(6))  // Round to 6 decimal places
    };
    
    setCurrentPosition(newPosition);
    setError('');
    
    // Reverse geocode to get address
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setSearchAddress(results[0].formatted_address);
          onCoordinatesChange?.(newPosition, results[0].formatted_address);
        } else {
          onCoordinatesChange?.(newPosition);
        }
      });
    } else {
      onCoordinatesChange?.(newPosition);
    }
  }, [onCoordinatesChange]);

  // Handle marker drag
  const handleMarkerDrag = useCallback((event) => {
    const newPosition = {
      lat: parseFloat(event.latLng.lat().toFixed(6)), // Round to 6 decimal places
      lng: parseFloat(event.latLng.lng().toFixed(6))  // Round to 6 decimal places
    };
    
    setCurrentPosition(newPosition);
    setError('');
    
    // Reverse geocode to get address
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setSearchAddress(results[0].formatted_address);
          onCoordinatesChange?.(newPosition, results[0].formatted_address);
        } else {
          onCoordinatesChange?.(newPosition);
        }
      });
    } else {
      onCoordinatesChange?.(newPosition);
    }
  }, [onCoordinatesChange]);

  // Search for address
  const handleAddressSearch = useCallback(() => {
    if (!searchAddress.trim() || !window.google || !window.google.maps) return;
    
    setIsSearching(true);
    setError('');
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchAddress }, (results, status) => {
      setIsSearching(false);
      
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const newPosition = {
          lat: parseFloat(location.lat().toFixed(6)), // Round to 6 decimal places
          lng: parseFloat(location.lng().toFixed(6))  // Round to 6 decimal places
        };
        
        setCurrentPosition(newPosition);
        setSearchAddress(results[0].formatted_address);
        onCoordinatesChange?.(newPosition, results[0].formatted_address);
      } else {
        setError('Address not found. Please try a different search term.');
      }
    });
  }, [searchAddress, onCoordinatesChange]);

  // Use current location
  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }
    
    setError('');
    setIsSearching(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newPosition = {
          lat: parseFloat(position.coords.latitude.toFixed(6)),  // Round to 6 decimal places
          lng: parseFloat(position.coords.longitude.toFixed(6))  // Round to 6 decimal places
        };
        
        setCurrentPosition(newPosition);
        
        // Reverse geocode to get address
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocoder({ location: newPosition }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setSearchAddress(results[0].formatted_address);
              onCoordinatesChange?.(newPosition, results[0].formatted_address);
            } else {
              onCoordinatesChange?.(newPosition);
            }
            setIsSearching(false);
          });
        } else {
          setIsSearching(false);
          onCoordinatesChange?.(newPosition);
        }
      },
      (err) => {
        console.error('Error getting current location:', err);
        setError('Failed to get current location. Please check your location permissions.');
        setIsSearching(false);
      }
    );
  }, [onCoordinatesChange]);

  // Handle Enter key in search
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddressSearch();
    }
  }, [handleAddressSearch]);

  return (
    <Box sx={{ width: width }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Venue Location
        </Typography>
        
        {/* Search Box */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            label="Search Address"
            variant="outlined"
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter address to search"
            disabled={isSearching}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button 
            variant="contained" 
            onClick={handleAddressSearch}
            disabled={isSearching || !searchAddress.trim()}
            sx={{ minWidth: '100px' }}
          >
            Search
          </Button>
        </Box>
        
        {/* Current Location Button */}
        <Button
          variant="outlined"
          startIcon={<MyLocationIcon />}
          onClick={handleUseCurrentLocation}
          disabled={isSearching}
          fullWidth
          sx={{ mb: 2 }}
        >
          Use My Current Location
        </Button>
        
        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Map */}
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={currentPosition}
            zoom={zoom}
            options={options}
            onClick={handleMapClick}
          >
            <Marker 
              position={currentPosition}
              draggable={true}
              onDragEnd={handleMarkerDrag}
              title="Drag to set exact location"
            />
          </GoogleMap>
        ) : (
          <Box 
            sx={{ 
              height: typeof height === 'number' ? `${height}px` : height, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              backgroundColor: 'action.hover',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading map...
            </Typography>
          </Box>
        )}
        
        {/* Instructions and Coordinates Display */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Click on the map or drag the marker to set the exact location. 
            You can also search for an address above.
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Typography variant="body2">
              <strong>Coordinates:</strong> {currentPosition.lat.toFixed(6)}, {currentPosition.lng.toFixed(6)}
            </Typography>
            
            {searchAddress && (
              <Typography variant="body2" sx={{ maxWidth: '60%' }}>
                <strong>Address:</strong> {searchAddress}
              </Typography>
            )}
          </Box>
          
          {/* Debug info */}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Map loaded: {isLoaded ? '✅' : '❌'} | 
            Marker position: {currentPosition.lat}, {currentPosition.lng}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

SimpleMapCoordinateSelector.propTypes = {
  initialCoordinates: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }),
  onCoordinatesChange: PropTypes.func,
  initialAddress: PropTypes.string,
  height: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  zoom: PropTypes.number,
  apiKey: PropTypes.string,
};

export default SimpleMapCoordinateSelector;